// Diese Datei wurde entfernt, da die Provisionslogik ausgelagert wurde.
import React, { useEffect, useMemo, useState } from 'react';
import { Search, Filter, Plus, Copy } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { phonebook, PhonebookEntry } from '../../data/phonebook';
import { useTheme } from '../../contexts/ThemeContext';
import { PageLayout, PageHeader, PageCard, pageStyles } from '../PageLayout';

type PhonebookViewProps = {
    currentUser: any;
};

const cleanPhone = (phone?: string) => {
    const cleaned = (phone || '').replace(/[^0-9+]/g, '').trim();
    return cleaned || '';
};

const PhonebookView: React.FC<PhonebookViewProps> = ({ currentUser }) => {
    const { theme } = useTheme();
    const isDark = theme === 'dark' || (theme === 'system' && window.matchMedia('(prefers-color-scheme: dark)').matches);
    const [searchTerm, setSearchTerm] = useState('');
    const [category, setCategory] = useState('');
    const [entries, setEntries] = useState<PhonebookEntry[]>(phonebook);
    const [categoriesList, setCategoriesList] = useState<string[]>(() => Array.from(new Set(phonebook.map(p => p.category).filter(Boolean))).sort());
    const [form, setForm] = useState({ category: '', topic: '', contact: '', phone: '', email: '', notes: '' });
    const [error, setError] = useState('');
    const [copiedId, setCopiedId] = useState<string | null>(null);
    const [openGroups, setOpenGroups] = useState<string[]>([]);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [renameFrom, setRenameFrom] = useState('');
    const [renameTo, setRenameTo] = useState('');
    const [deleteTarget, setDeleteTarget] = useState('');

    const filtered = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        return entries.filter((entry: PhonebookEntry) => {
            if (category && entry.category !== category) return false;
            if (!term) return true;
            return [
                entry.topic,
                entry.contact,
                entry.category,
                entry.phone,
                entry.email,
                entry.notes,
            ].some(value => (value || '').toLowerCase().includes(term));
        });
    }, [searchTerm, category, entries]);

    const sortedFiltered = useMemo(() => {
        return [...filtered].sort((a, b) => {
            if (a.category === b.category) return a.topic.localeCompare(b.topic);
            return a.category.localeCompare(b.category);
        });
    }, [filtered]);

    const grouped = useMemo(() => {
        const by: Record<string, PhonebookEntry[]> = {};
        sortedFiltered.forEach(entry => {
            const key = entry.category || 'Allgemein';
            if (!by[key]) by[key] = [];
            by[key].push(entry);
        });
        return by;
    }, [sortedFiltered]);


    const isAdmin = currentUser?.role === 'admin';

    const addEntry = () => {
        setError('');
        if (!form.topic.trim()) {
            setError('Thema wird benötigt.');
            return;
        }
        if (!form.phone.trim() && !form.email.trim()) {
            setError('Mindestens Telefon oder E-Mail angeben.');
            return;
        }
        const newEntry: PhonebookEntry = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            category: form.category || 'Allgemein',
            topic: form.topic.trim(),
            contact: form.contact.trim(),
            phone: form.phone.trim(),
            email: form.email.trim(),
            notes: form.notes.trim() || undefined,
        };
        setEntries(prev => [newEntry, ...prev]);
        if (newEntry.category && !categoriesList.includes(newEntry.category)) {
            setCategoriesList(prev => [...prev, newEntry.category].sort());
        }
        setForm({ category: '', topic: '', contact: '', phone: '', email: '', notes: '' });
    };

    const addCategoryNameHandler = () => {
        const name = newCategoryName.trim();
        if (!name) return;
        if (!categoriesList.includes(name)) {
            setCategoriesList(prev => [...prev, name].sort());
        }
        setNewCategoryName('');
    };

    const renameCategoryHandler = () => {
        const from = renameFrom.trim();
        const to = renameTo.trim();
        if (!from || !to || from === to) return;
        setEntries(prev => prev.map(e => e.category === from ? { ...e, category: to } : e));
        setCategoriesList(prev => {
            const filtered = prev.filter(c => c !== from);
            const next = filtered.includes(to) ? filtered : [...filtered, to];
            return next.sort();
        });
        if (category === from) setCategory(to);
        setRenameFrom('');
        setRenameTo('');
    };

    const deleteCategoryHandler = () => {
        const target = deleteTarget.trim();
        if (!target) return;
        setEntries(prev => prev.map(e => e.category === target ? { ...e, category: 'Allgemein' } : e));
        setCategoriesList(prev => prev.filter(c => c !== target));
        if (category === target) setCategory('');
        setDeleteTarget('');
    };

    const moveEntryToCategory = (id: string, newCat: string) => {
        setEntries(prev => prev.map(e => e.id === id ? { ...e, category: newCat } : e));
        if (newCat && !categoriesList.includes(newCat)) {
            setCategoriesList(prev => [...prev, newCat].sort());
        }
    };

    const copyPhone = async (phone: string, id: string) => {
        if (!phone) return;
        try {
            await navigator.clipboard?.writeText(phone);
        } catch (err) {
            // clipboard not available; ignore
        }
        setCopiedId(id);
    };

    useEffect(() => {
        if (!copiedId) return;
        const timer = setTimeout(() => setCopiedId(null), 1500);
        return () => clearTimeout(timer);
    }, [copiedId]);

    useEffect(() => {
        setOpenGroups([]);
    }, [grouped]);

    useEffect(() => {
        setCategoriesList(prev => {
            const derived = Array.from(new Set(entries.map(p => p.category).filter(Boolean)));
            const merged = Array.from(new Set([...prev, ...derived])).sort();
            return merged;
        });
    }, [entries]);

    return (
        <PageLayout>
            <PageHeader
                title={`Telefonbuch (${filtered.length})`}
                subtitle={!isAdmin ? 'Wichtige Ansprechpartner der GD München. Leserechte für Team; Änderungen nur durch Admin.' : 'Wichtige Ansprechpartner der GD München'}
                actions={
                <div className="flex flex-col md:flex-row gap-3 w-full md:w-auto">
                    <div className="relative w-full md:w-64">
                        <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/60' : 'text-slate-400'}`} />
                        <input
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder="Thema, Kontakt, Telefon, Mail"
                            className={`w-full rounded-xl pl-9 pr-10 py-2 text-sm outline-none focus:ring-2 focus:ring-blue-400/50 transition-colors ${isDark ? 'bg-white/10 backdrop-blur-sm border border-white/20 text-white placeholder-white/50 focus:border-blue-400/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-blue-400/50'}`}
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className={`absolute right-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/50 hover:text-white/70' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                ×
                            </button>
                        )}
                    </div>
                    <div className="relative w-full md:w-48">
                        <Filter size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-white/50' : 'text-slate-400'}`} />
                        <select
                            value={category}
                            onChange={e => setCategory(e.target.value)}
                            className={`w-full rounded-xl pl-9 pr-8 py-2 text-sm outline-none focus:ring-2 focus:ring-cyan-400/50 appearance-none cursor-pointer ${isDark ? 'glass-input border-white/20 text-white bg-white/5' : 'bg-slate-50 border border-slate-200 text-slate-900'}`}
                        >
                            <option value="">Alle Kategorien</option>
                            {categoriesList.map(cat => (
                                <option key={cat} value={cat}>{cat}</option>
                            ))}
                        </select>
                        <span className={`absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none ${isDark ? 'text-white/50' : 'text-slate-400'}`}>▾</span>
                    </div>
                </div>
                }
            />

            {isAdmin && (
                <PageCard className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                        <h3 className={`text-lg font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}><Plus size={18} className="text-cyan-400"/>Neuer Eintrag</h3>
                        <span className={`text-xs ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Nur Admin</span>
                    </div>
                    <p className={`text-xs ${isDark ? 'text-white/70' : 'text-slate-600'}`}>Pflichtfelder: Thema, mindestens Telefon oder E-Mail.</p>
                    {error && <div className={`text-sm rounded-xl px-3 py-2 ${isDark ? 'text-red-300 bg-red-500/20 border border-red-400/30' : 'text-red-700 bg-red-50 border border-red-200'}`}>{error}</div>}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <select aria-label="Kategorie" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))} className={`rounded-xl px-3 py-2 text-sm cursor-pointer ${isDark ? 'glass-input border-white/20 text-white bg-white/5 placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`}>
                            <option value="">Kategorie wählen/neu tippen</option>
                            {categoriesList.map(cat => <option key={cat} value={cat} className={isDark ? "bg-gray-900 text-white" : "bg-white text-slate-900"}>{cat}</option>)}
                        </select>
                        <input aria-label="Thema" value={form.topic} onChange={e => setForm(f => ({ ...f, topic: e.target.value }))} className={`rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`} placeholder="Thema*" />
                        <input aria-label="Kontakt" value={form.contact} onChange={e => setForm(f => ({ ...f, contact: e.target.value }))} className={`rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`} placeholder="Kontakt/Team" />
                        <input aria-label="Telefon" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className={`rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`} placeholder="Telefon" />
                        <input aria-label="E-Mail" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={`rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`} placeholder="E-Mail" />
                        <input aria-label="Notiz" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className={`rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`} placeholder="Notiz" />
                    </div>
                    <div className="flex justify-end">
                        <button onClick={addEntry} className={`px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition-colors ${isDark ? 'bg-cyan-500/50 border-cyan-400/80 text-white hover:bg-cyan-500/60' : 'bg-cyan-100 border border-cyan-300 text-cyan-700 hover:bg-cyan-200'}`}><Plus size={16}/>Speichern</button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border-t pt-3" style={{borderColor: isDark ? 'rgb(229, 231, 235, 0.1)' : 'rgb(203, 213, 225)'}}>
                        <div className="space-y-2">
                            <p className={`text-xs font-bold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Kategorie anlegen</p>
                            <div className="flex flex-col gap-2">
                                <input value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)} className={`rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`} placeholder="Neue Kategorie" />
                                <button onClick={addCategoryNameHandler} className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${isDark ? 'bg-white/10 hover:bg-white/20 text-white/80' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}>Hinzufügen</button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <p className={`text-xs font-bold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Kategorie umbenennen</p>
                            <div className="flex flex-col gap-2">
                                <select value={renameFrom} onChange={e => setRenameFrom(e.target.value)} className={`rounded-xl px-3 py-2 text-sm cursor-pointer ${isDark ? 'glass-input border-white/20 text-white bg-white/5' : 'bg-slate-50 border border-slate-200 text-slate-900'}`}>
                                    <option value="">Quelle wählen</option>
                                    {categoriesList.map(cat => <option key={cat} value={cat} className={isDark ? "bg-gray-900 text-white" : "bg-white text-slate-900"}>{cat}</option>)}
                                </select>
                                <input value={renameTo} onChange={e => setRenameTo(e.target.value)} className={`rounded-xl px-3 py-2 text-sm ${isDark ? 'glass-input border-white/20 text-white placeholder-white/50' : 'bg-slate-50 border border-slate-200 text-slate-900 placeholder-slate-400'}`} placeholder="Neuer Name" />
                                <button onClick={renameCategoryHandler} className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${isDark ? 'bg-cyan-500/50 border-cyan-400/80 text-white hover:bg-cyan-500/60' : 'bg-cyan-100 border border-cyan-300 text-cyan-700 hover:bg-cyan-200'}`}>Umbenennen</button>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <p className={`text-xs font-bold ${isDark ? 'text-white/80' : 'text-slate-700'}`}>Kategorie löschen</p>
                            <div className="flex flex-col gap-2">
                                <select value={deleteTarget} onChange={e => setDeleteTarget(e.target.value)} className={`rounded-xl px-3 py-2 text-sm cursor-pointer ${isDark ? 'glass-input border-white/20 text-white bg-white/5' : 'bg-slate-50 border border-slate-200 text-slate-900'}`}>
                                    <option value="">Kategorie wählen</option>
                                    {categoriesList.map(cat => <option key={cat} value={cat} className={isDark ? "bg-gray-900 text-white" : "bg-white text-slate-900"}>{cat}</option>)}
                                </select>
                                <button onClick={deleteCategoryHandler} className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${isDark ? 'bg-red-500/50 border-red-400/80 text-white hover:bg-red-500/60' : 'bg-red-100 border border-red-300 text-red-700 hover:bg-red-200'}`}>Löschen</button>
                            </div>
                            <p className={`text-[11px] ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Löschen verschiebt Einträge nach „Allgemein".</p>
                        </div>
                    </div>
                </PageCard>
            )}

                <PageCard>
                <Accordion type="multiple" value={openGroups} onValueChange={(v) => setOpenGroups(v as string[])} className={`divide-y ${isDark ? 'divide-white/10' : 'divide-slate-200'}`}>
                    {Object.entries(grouped).map(([cat, list]) => (
                        <AccordionItem key={cat} value={cat} className="border-b last:border-b-0">
                            <AccordionTrigger className="px-4">
                                <div className="flex items-center gap-3">
                                    <span className={`font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{cat}</span>
                                    <span className={`text-xs px-2 py-1 rounded-full ${isDark ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400/30' : 'bg-cyan-100 text-cyan-700 border border-cyan-300'}`}>{list.length}</span>
                                </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-4 pb-4">
                                <div className={`hidden md:grid grid-cols-12 px-1 pb-2 text-[11px] font-bold uppercase tracking-wide ${isDark ? 'text-white/60' : 'text-slate-600'}`}>
                                    <span className="col-span-3">Thema</span>
                                    <span className="col-span-3">Kontakt</span>
                                    <span className="col-span-3">Telefon</span>
                                    <span className="col-span-3">E-Mail</span>
                                </div>
                                <div className={`divide-y ${isDark ? 'divide-white/10' : 'divide-slate-200'}`}>
                                    {list.map(entry => {
                                        const telHref = cleanPhone(entry.phone) ? `tel:${cleanPhone(entry.phone)}` : '';
                                        const mailHref = entry.email ? `mailto:${entry.email}` : '';
                                        return (
                                            <div
                                                key={entry.id}
                                                className={`grid grid-cols-1 md:grid-cols-12 py-3 gap-y-2 items-start text-sm ${isDark ? 'text-white/80' : 'text-slate-700'}`}
                                            >
                                                <div className="md:col-span-3 min-w-0">
                                                    <div className={`font-bold truncate ${isDark ? 'text-white' : 'text-slate-900'}`}>{entry.topic}</div>
                                                    {entry.notes && <div className={`text-[11px] inline-block px-2 py-0.5 rounded-full mt-1 border ${isDark ? 'text-cyan-300 bg-cyan-500/20 border-cyan-400/30' : 'text-cyan-700 bg-cyan-100 border-cyan-300'}`}>Hinweis</div>}
                                                </div>
                                                <div className={`md:col-span-3 truncate ${isDark ? 'text-white/70' : 'text-slate-600'}`}>{entry.contact || '-'}</div>
                                                <div className="md:col-span-3 flex items-center gap-2">
                                                    {telHref ? (
                                                        <a className={`font-semibold hover:underline ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`} href={telHref}>{entry.phone}</a>
                                                    ) : (
                                                        <span className={isDark ? 'text-white/50' : 'text-slate-400'}>-</span>
                                                    )}
                                                    {entry.phone && (
                                                        <button onClick={() => copyPhone(entry.phone, entry.id)} className={`transition-colors ${isDark ? 'text-white/50 hover:text-white/80' : 'text-slate-400 hover:text-slate-600'}`} title="Nummer kopieren">
                                                            {copiedId === entry.id ? <span className={`text-[11px] font-semibold ${isDark ? 'text-green-300' : 'text-green-600'}`}>Kopiert</span> : <Copy size={14} />}
                                                        </button>
                                                    )}
                                                </div>
                                                <div className="md:col-span-3 flex flex-col gap-2">
                                                    {mailHref ? (
                                                        <a className={`font-semibold hover:underline break-all ${isDark ? 'text-cyan-300' : 'text-cyan-600'}`} href={mailHref}>{entry.email}</a>
                                                    ) : (
                                                        <span className={isDark ? 'text-white/50' : 'text-slate-400'}>-</span>
                                                    )}
                                                    {isAdmin && (
                                                        <select value={entry.category} onChange={e => moveEntryToCategory(entry.id, e.target.value)} className={`rounded-xl px-3 py-2 text-xs cursor-pointer ${isDark ? 'glass-input border-white/20 text-white bg-white/5' : 'bg-slate-50 border border-slate-200 text-slate-900'}`}>
                                                            {categoriesList.map(catOption => <option key={catOption} value={catOption} className={isDark ? "bg-gray-900 text-white" : "bg-white text-slate-900"}>{catOption}</option>)}
                                                        </select>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    ))}
                    {filtered.length === 0 && (
                        <div className={`px-4 py-6 text-center text-sm ${isDark ? 'text-white/50' : 'text-slate-500'}`}>Keine Einträge gefunden.</div>
                    )}
                </Accordion>
            </PageCard>
        </PageLayout>
    );
};

export default PhonebookView;
